import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import MonthSelector from '../components/common/MonthSelector';
import Layout from '../components/layout/Layout';
import { useCurrency } from '../context/CurrencyContext';
import { useTelegram } from '../context/TelegramContext';
import { fetchDashboardData } from '../services/dashboardService';
import commonStyles from '../styles/common.module.css';
import dashboardStyles from '../styles/components/Dashboard.module.css';
import ExchangeRate from '../components/dashboard/ExchangeRate';
import TransactionModal from '../components/modals/TransactionModal';
import styled from 'styled-components';
import NotificationCenter from '../components/dashboard/NotificationCenter';


// Мягкое изменение фона для отрицательного баланса
const NoZoomContainer = styled.div`
  touch-action: pan-x pan-y; /* Запрещаем масштабирование, но разрешаем прокрутку */
  overflow-y: auto;
  height: 100%;
  width: 100%;
  
`;
// 1. Уменьшенные размеры для карточек
const EnhancedCard = styled.div`
  background: ${props => {
    if (props.type === 'balance' && props.$negative) {
      return 'linear-gradient(145deg, #fff8f8, #fff5f5)';
    }
    return 'linear-gradient(145deg, #ffffff, #f5f6fa)';
  }};
  border-radius: 14px; // Уменьшено с 16px
  padding: 18px 16px; // Уменьшено с 22px 18px
  box-shadow: 
    4px 4px 12px rgba(0, 0, 0, 0.06),
    -4px -4px 12px rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  min-height: 100px; // Уменьшено с 115px
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px); // Уменьшено с -3px
    box-shadow: 
      6px 6px 16px rgba(0, 0, 0, 0.08),
      -6px -6px 16px rgba(255, 255, 255, 0.9);
  }
  
  h2 {
    font-size: 22px; // Уменьшено с 26px
    font-weight: 600;
    margin: 0 0 4px; // Уменьшено с 0 0 5px
    color: ${props => {
      if (props.type === 'balance' && props.$negative) return '#c0392b';
      if (props.type === 'balance') return 'var(--income-text, #337ab7)';
      if (props.type === 'income') return 'var(--primary-color, #4CAF50)';
      if (props.type === 'expense') return 'var(--expense-text, #c0392b)';
      return '#5C6BC0';
    }};
    text-shadow: 1px 1px 2px rgba(255, 255, 255, 0.8);
  }
  
  p {
    color: #8c96a5;
    margin: 0;
    font-size: 13px; // Уменьшено с 14px
    font-weight: 500;
  }
`;

// 2. Более компактный индикатор отрицательного баланса
const NegativeBalanceIndicator = styled.div`
  margin-top: 8px; // Уменьшено с 10px
  font-size: 11px; // Уменьшено с 12px
  color: #c0392b;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px; // Уменьшено с 4px
  
  svg {
    width: 12px; // Уменьшено с 14px
    height: 12px; // Уменьшено с 14px
  }
`;

// 3. Компактная версия DailyBudgetInfo
const DailyBudgetInfo = styled.div`
  margin-top: 10px; // Уменьшено с 14px
  border-radius: 10px; // Уменьшено с 12px
  padding: 7px 10px; // Уменьшено с 10px 14px
  background: #ffffff;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 
    inset 1px 1px 3px rgba(0, 0, 0, 0.05),
    inset -1px -1px 3px rgba(255, 255, 255, 0.9);
`;

// 4. Уменьшение карточки с днями до зарплаты
const DaysToSalaryCard = styled(EnhancedCard)`
  .days-circle {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 70px; // Уменьшено с 80px
    height: 70px; // Уменьшено с 80px
    margin: 3px 0 8px; // Уменьшено с 5px 0 10px
  }
  
  .circle-bg {
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: #f0f0f0;
  }
  
  .circle-progress {
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: conic-gradient(
      #4a6fa1 ${props => (props.$daysPercent || 0) * 3.6}deg,
      transparent 0deg
    );
    transition: background 0.3s ease;
  }
  
  .circle-inner {
    position: absolute;
    width: 70%;
    height: 70%;
    border-radius: 50%;
    background: white;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05); // Уменьшено с 0 2px 6px
  }
  
  .days-number {
    font-size: 22px; // Уменьшено с 24px
    font-weight: 600;
    color: #4a6fa1;
  }
  
  &:before {
    background: linear-gradient(90deg, #a1c4fd, #c2e9fb);
  }
`;

// 5. Уменьшение размера кнопок действий
const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 10px; // Уменьшено с 12px
  margin: 14px 0; // Уменьшено с 20px 0
  width: 100%;
`;

const ActionButton = styled.button`
  flex: 1;
  border: none;
  border-radius: 10px; // Уменьшено с 12px
  padding: 10px 14px; // Уменьшено с 12px 16px
  font-size: 14px; // Уменьшено с 15px
  font-weight: 500;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px; // Уменьшено с 10px
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1); // Уменьшена тень
  
  /* Используем фирменные цвета из CSS-переменных */
  background: ${props => props.type === 'income' 
    ? 'var(--income-text, #337ab7)' 
    : 'var(--expense-text, #c0392b)'};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${props => props.type === 'income' 
      ? 'rgba(51, 122, 183, 0.25)' 
      : 'rgba(192, 57, 43, 0.25)'}; // Уменьшена интенсивность тени
    filter: brightness(1.05);
  }
  
  &:active {
    transform: translateY(1px);
  }
  
  svg {
    width: 16px; // Уменьшено с 18px
    height: 16px; // Уменьшено с 18px
    fill: currentColor;
  }
`;

const NavButtonsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px; // Уменьшено с 10px для десктопа
  width: 100%;
  margin-top: 12px;
  
  @media (max-width: 576px) {
    gap: 10px;
    margin-top: 16px; // Увеличиваем отступ на мобильных
  }
`;

const NavButton = styled.button`
  border: none;
  border-radius: 10px;
  padding: 8px; // Базовый отступ для десктопа
  background: white;
  color: #4a6fa1;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
  position: relative;
  overflow: hidden;
  
  &:hover {
    background: #f8fafc;
    transform: translateY(-2px);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.06);
    
    &:after {
      opacity: 1;
    }
  }
  
  &:active {
    transform: translateY(1px);
  }
  
  /* Эффект свечения при наведении */
  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, #6a11cb, #2575fc);
    opacity: 0;
    transition: opacity 0.2s ease;
  }
  
  svg {
    width: 14px;
    height: 14px;
    fill: currentColor;
  }
  
  /* Стили для мобильных устройств */
  @media (max-width: 576px) {
    padding: 12px; // Увеличиваем padding
    font-size: 14px; // Увеличиваем размер шрифта
    gap: 8px; // Увеличиваем отступ между иконкой и текстом
    height: 50px; // Фиксируем высоту кнопки для удобного нажатия
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.06); // Усиливаем тень
    
    svg {
      width: 18px; // Увеличиваем иконки
      height: 18px;
    }
    
    &:after {
      height: 3px; // Увеличиваем полосу свечения
    }
  }
`;
// Убираем все старые стили для уведомлений из HeaderContainer
const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin-bottom: 14px;
  position: relative;
  justify-content: center; /* Центрируем селектор месяца */
`;

// Центральная часть с селектором месяца
const HeaderCenter = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
`;

// Правая часть только с аватаркой (убираем уведомления)
const HeaderRight = styled.div`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
`;

// Обновляем UserAvatar - убираем сложности с уведомлениями
const UserAvatar = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  
  /* Неоморфная стилизация */
  padding: 4px;
  background: linear-gradient(145deg, #f0f3f9, #ffffff);
  box-shadow: 
    5px 5px 15px rgba(174, 174, 192, 0.45),
    -8px -8px 40px rgba(255, 255, 255, 1),
    inset 1px 1px 2px rgba(255, 255, 255, 0.8),
    inset -1px -1px 2px rgba(174, 174, 192, 0.15);
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 
      8px 8px 20px rgba(174, 174, 192, 0.55),
      -8px -8px 20px rgba(255, 255, 255, 1),
      inset 1px 1px 2px rgba(255, 255, 255, 0.8),
      inset -1px -1px 2px rgba(174, 174, 192, 0.15);
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
    transition: all 0.3s ease;
  }
  
  @media (max-width: 576px) {
    width: 34px;
    height: 34px;
  }
`;

// Удаляем все старые стили для уведомлений:
// - NotificationBadgeOnAvatar
// - UnreadCountBadge  
// - AvatarWithNotifications
// - NotificationCenterContainer

// Новый контейнер для ExchangeRate с уведомлениями
const ExchangeRateWithNotifications = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  gap: 12px;
  
  @media (max-width: 576px) {
    gap: 8px;
  }
`;

// Контейнер для иконки уведомлений рядом с курсом валют
const NotificationIconContainer = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;
// 7. Уменьшение компонента выбора месяца
const StyledMonthSelector = styled.div`
  display: flex;
  align-items: center;
  background: linear-gradient(145deg, #ffffff, #f5f6fa);
  border-radius: 14px; // Уменьшено с 16px
  padding: 4px 8px; // Уменьшено с 6px 10px
  box-shadow: 
    3px 3px 6px rgba(0, 0, 0, 0.05),
    -3px -3px 6px rgba(255, 255, 255, 0.8);
  max-width: 280px;
  width: 100%;
`;

const MonthButton = styled.button`
  background: none;
  border: none;
  font-size: 16px; // Уменьшено с 18px
  color: #6c7d95;
  cursor: pointer;
  padding: 6px; // Уменьшено с 8px
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s, color 0.2s;
  border-radius: 50%;
  width: 32px; // Уменьшено с 36px
  height: 32px; // Уменьшено с 36px
  
  &:hover {
    color: #4a6fa1;
    background: rgba(255, 255, 255, 0.5);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
  
  &:active {
    transform: scale(0.95);
    background: rgba(0, 0, 0, 0.03);
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    box-shadow: none;
    
    &:hover {
      color: #6c7d95;
      background: none;
      transform: none;
    }
  }
  
  svg {
    width: 20px; // Уменьшено с 22px
    height: 20px; // Уменьшено с 22px
    fill: currentColor;
  }
`;

const MonthText = styled.div`
  flex: 1;
  text-align: center;
  font-weight: 600;
  font-size: 15px; // Уменьшено с 16px
  color: #4a6fa1;
  padding: 0 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  /* Красивое подчеркивание текущего месяца */
  &:after {
    content: '';
    position: absolute;
    bottom: -3px; // Уменьшено с -4px
    left: 50%;
    transform: translateX(-50%);
    width: 40%;
    height: 2px;
    background: linear-gradient(90deg, #6a11cb, #2575fc);
    border-radius: 2px;
    transition: width 0.2s;
  }
  
  &:hover:after {
    width: 60%;
  }
`;

// 8. Компактные информационные блоки
const ComparisonInfo = styled.div`
  margin-top: 10px; // Уменьшено с 14px
  border-radius: 10px; // Уменьшено с 12px
  padding: 7px 8px; // Уменьшено с 10px
  width: 100%;
  text-align: center;
  font-size: 12px; // Уменьшено с 13px
  
  ${props => props.type === 'positive' ? `
    background: #f0fff4;
    color: #38a169;
    box-shadow: inset 2px 2px 5px rgba(56, 161, 105, 0.05), inset -2px -2px 5px rgba(255, 255, 255, 0.9);
  ` : `
    background: #fff5f5;
    color: #e53e3e;
    box-shadow: inset 2px 2px 5px rgba(229, 62, 62, 0.05), inset -2px -2px 5px rgba(255, 255, 255, 0.9);
  `}
`;



// 11. Оптимизация переключателя общих данных
const StyledSwitchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px; // Уменьшено с 10px
  margin: 4px 0 10px; // Уменьшено с 6px 0 12px
  font-size: 12px; // Уменьшено с 13px
  color: #6c7d95;
  padding: 6px 10px; // Уменьшено с 8px 12px
  border-radius: 10px;
  background: linear-gradient(145deg, #f9fafc, #f0f3f9);
  box-shadow: 
    inset 1px 1px 2px rgba(174, 174, 192, 0.2),
    inset -1px -1px 2px rgba(255, 255, 255, 0.7);
`;

const SwitchToggle = styled.label`
  position: relative;
  display: inline-block;
  width: 32px; // Уменьшено с 36px
  height: 16px; // Уменьшено с 18px
  flex-shrink: 0;
  
  input {
    opacity: 0;
    width: 0;
    height: 0;
    
    &:checked + .slider {
      background: linear-gradient(145deg, #6a11cb, #2575fc);
      
      &:before {
        transform: translateX(16px); // Уменьшено с 18px
      }
    }
  }
  
  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: #e0e5ec;
    box-shadow: inset 2px 2px 4px rgba(0, 0, 0, 0.05),
                inset -2px -2px 4px rgba(255, 255, 255, 0.5);
    border-radius: 16px;
    transition: all 0.3s ease;
    
    &:before {
      position: absolute;
      content: "";
      height: 12px; // Уменьшено с 14px
      width: 12px; // Уменьшено с 14px
      left: 2px;
      bottom: 2px;
      background: white;
      border-radius: 50%;
      transition: all 0.3s ease;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
    }
  }
`;

// 13. Уменьшение размера буджета
const BudgetLabel = styled.span`
  font-size: 11px; // Уменьшено с 12px
  color: #4a6fa1;
  font-weight: 500;
`;

const BudgetValue = styled.span`
  font-size: 12px; // Уменьшено с 13px
  font-weight: 600;
  color: #2575fc;
  background: rgba(37, 117, 252, 0.08);
  padding: 2px 5px; // Уменьшено с 2px 7px
  border-radius: 6px; // Уменьшено с 8px
`;
// Компактный индикатор отрицательного баланса



// Иконка внимания для отрицательного баланса
const WarningIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 5.99L19.53 19H4.47L12 5.99M12 2L1 21h22L12 2zm1 14h-2v2h2v-2zm0-6h-2v4h2v-4z"/>
  </svg>
);


// Иконки
const PlusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/>
  </svg>
);

const MinusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11H7v-2h10v2z"/>
  </svg>
);


// Иконки для навигационных кнопок
const StatsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
    <path d="M9 17H7V10H9V17ZM13 17H11V7H13V17ZM17 17H15V13H17V17ZM19 19H5V5H19V19ZM19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3Z"/>
  </svg>
);

const GoalsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
    <path d="M19.07 4.93C17.22 3.08 14.66 2 12 2C9.34 2 6.78 3.08 4.93 4.93C3.08 6.78 2 9.34 2 12C2 14.66 3.08 17.22 4.93 19.07C6.78 20.92 9.34 22 12 22C14.66 22 17.22 20.92 19.07 19.07C20.92 17.22 22 14.66 22 12C22 9.34 20.92 6.78 19.07 4.93ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z"/>
  </svg>
);
// Специальная карточка для дней до зарплаты с круговым прогресс-баром




const SwitchLabel = styled.span`
  font-weight: 500;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;





// Скелетон для карточек при загрузке
const SkeletonCard = styled.div`
  background: linear-gradient(to right, #f9f9f9, #fff);
  border-radius: 14px;
  padding: 18px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  height: 100%;
  min-height: 140px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  
  /* Имитация градиентной обводки */
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 14px;
    padding: 2px;
    background: linear-gradient(45deg, #f0f0f0, #e8e8e8, #f0f0f0);
    -webkit-mask: 
      linear-gradient(#fff 0 0) content-box, 
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    z-index: 1;
  }
  
  /* Улучшенная анимация блеска */
  &:after {
    content: "";
    position: absolute;
    top: -50%;
    left: -50%;
    right: -50%;
    bottom: -50%;
    background: linear-gradient(
      to right,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.1) 30%,
      rgba(255, 255, 255, 0.2) 50%,
      rgba(255, 255, 255, 0.1) 70%,
      rgba(255, 255, 255, 0) 100%
    );
    transform: rotate(45deg) translateY(-50%) translateX(-50%);
    animation: shinyEffect 2.5s infinite cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  @keyframes shinyEffect {
    0% {
      transform: rotate(45deg) translate(-30%, -100%);
    }
    50% {
      transform: rotate(45deg) translate(-30%, 100%);
    }
    100% {
      transform: rotate(45deg) translate(-30%, 300%);
    }
  }
`;

// Обновленный заголовок скелетона с анимацией цвета
const SkeletonTitle = styled.div`
  height: 32px;
  width: 70%;
  background: linear-gradient(-60deg, #f0f0f0, #f8f8f8, #f0f0f0);
  background-size: 200% 200%;
  border-radius: 6px;
  margin-bottom: 14px;
  animation: gradientShift 2s ease infinite;
  
  @keyframes gradientShift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
`;

// Обновленный текст скелетона
const SkeletonText = styled.div`
  height: 18px;
  width: 50%;
  background: linear-gradient(-60deg, #f0f0f0, #f8f8f8, #f0f0f0);
  background-size: 200% 200%;
  border-radius: 4px;
  margin-bottom: 10px;
  animation: gradientShift 2s ease infinite;
  animation-delay: 0.2s;
`;

// Добавим дополнительный элемент для скелетона - имитация ежедневного бюджета
const SkeletonBudget = styled.div`
  margin-top: 8px;
  height: 24px;
  width: 90%;
  background: linear-gradient(-60deg, #f0f0f0, #f8f8f8, #f0f0f0);
  background-size: 200% 200%;
  border-radius: 6px;
  animation: gradientShift 2s ease infinite;
  animation-delay: 0.4s;
`;

// Обновленная кнопка скелетона
const SkeletonButton = styled.div`
  height: 50px;
  width: 100%;
  background: linear-gradient(-60deg, #f0f0f0, #f8f8f8, #f0f0f0);
  background-size: 200% 200%;
  border-radius: 10px;
  margin-top: 12px;
  position: relative;
  overflow: hidden;
  animation: gradientShift 2s ease infinite;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.03);
`;

// Анимированный блок для navigation buttons
const SkeletonNavButton = styled.div`
  height: 45px;
  width: 48%;
  background: linear-gradient(-60deg, #f0f0f0, #f8f8f8, #f0f0f0);
  background-size: 200% 200%;
  border-radius: 8px;
  animation: gradientShift 2s ease infinite;
  animation-delay: 0.6s;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.03);
`;

// Обновленный контейнер для navigation buttons
const SkeletonNavContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 15px;
  width: 100%;
`;

// Улучшенный текст статуса загрузки с анимацией точек
const LoadingStatusText = styled.div`
  text-align: center;
  color: #5a6270;
  font-size: 15px;
  font-weight: 500;
  margin: 15px 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  
  /* Анимированный индикатор */
  &:before {
    content: "";
    display: inline-block;
    width: 18px;
    height: 18px;
    border: 2px solid rgba(106, 17, 203, 0.2);
    border-radius: 50%;
    border-top-color: #6a11cb;
    animation: spin 1s linear infinite;
  }
  
  /* Анимация точек */
  &:after {
    content: "...";
    animation: dots 1.5s infinite;
    width: 24px;
    text-align: left;
  }
  
  @keyframes dots {
    0% { content: "."; }
    33% { content: ".."; }
    66% { content: "..."; }
    100% { content: "."; }
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;
// Обновляем стиль для MonthSelectorWrapper

const MonthSelectorWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 0 36px;
  margin-bottom: 6px;
`;

// Добавляем новые стилизованные компоненты для селектора месяца



// Иконки для кнопок месяца
const ChevronLeftIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
    <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
  </svg>
);

const ChevronRightIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
    <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
  </svg>
);

const CURRENCY_SYMBOLS = {
  'USD': '$',
  'EUR': '€',
  'UAH': '₴',
  'RUB': '₽',
  'GBP': '£',
  'JPY': '¥',
  'CNY': '¥',
  'INR': '₹',
  'CAD': 'C$',
  'AUD': 'A$',
  'BRL': 'R$',
  'CHF': 'Fr',
  'PLN': 'zł',
  'TRY': '₺'
};
// Локальная функция форматирования валюты
// Обновленная функция форматирования валюты
const formatCurrency = (amount, currencyCode = 'USD') => {
  if (amount === null || amount === undefined) {
    return '0.00';
  }
  
  const numAmount = Number(amount);
  if (isNaN(numAmount)) {
    return '0.00';
  }
  
  // Форматируем число с двумя десятичными знаками
  const formattedNumber = new Intl.NumberFormat(undefined, {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numAmount);
  
  // Получаем символ валюты из нашего объекта CURRENCY_SYMBOLS
  const currencySymbol = CURRENCY_SYMBOLS[currencyCode] || currencyCode;
  
  // Для определенных валют размещаем символ после числа
  if (['UAH', 'RUB', 'PLN'].includes(currencyCode)) {
    return `${formattedNumber} ${currencySymbol}`;
  } 
  // Для остальных валют - перед числом
  else {
    return `${currencySymbol}${formattedNumber}`;
  }
};

// Создаем компонент сообщения об ошибке авторизации
const AuthErrorDisplay = ({ t, onRefresh }) => (
  <div className={commonStyles.container} style={{ 
    display: 'flex', 
    flexDirection: 'column', 
    alignItems: 'center', 
    justifyContent: 'center', 
    height: '100vh',
    padding: '20px',
    textAlign: 'center'
  }}>
    <div style={{ marginBottom: '20px', color: '#c0392b' }}>
      <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
      </svg>
    </div>
    <h2 style={{ color: '#4a6fa1', marginBottom: '10px' }}>{t('authorization_error') || 'Ошибка авторизации'}</h2>
    <p style={{ color: '#6c7d95', marginBottom: '20px' }}>{t('please_open_in_telegram') || 'Пожалуйста, откройте приложение через Telegram'}</p>
    <button 
      onClick={onRefresh} 
      style={{ 
        background: '#4a6fa1',
        color: 'white',
        border: 'none',
        padding: '10px 20px',
        borderRadius: '8px',
        cursor: 'pointer'
      }}
    >
      {t('refresh') || 'Обновить'}
    </button>
  </div>
);

const Dashboard = () => {
  const { t } = useTranslation();
  const { userCurrency } = useCurrency();
  const { telegramUserId, userPhotoUrl, isInitialized } = useTelegram();
  const navigate = useNavigate(); // Хук для навигации
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [includeSharedData, setIncludeSharedData] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [transactionType, setTransactionType] = useState('');
  const [dashboardData, setDashboardData] = useState({
    balance: { amount: 0, currency: userCurrency },
    income: { amount: 0, currency: userCurrency },
    expenses: { amount: 0, currency: userCurrency },
    daysToSalary: 0,
    incomeComparison: null,
    expensesComparison: null
  });
  const [hasLinkedAccounts, setHasLinkedAccounts] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    // Находим или создаем метатег viewport
    let viewportMeta = document.querySelector('meta[name="viewport"]');
    const originalContent = viewportMeta ? viewportMeta.getAttribute('content') : '';
    
    if (!viewportMeta) {
      viewportMeta = document.createElement('meta');
      viewportMeta.name = 'viewport';
      document.head.appendChild(viewportMeta);
    }
    
    // Устанавливаем запрет масштабирования
    viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
    
    // Восстанавливаем оригинальное состояние при размонтировании компонента
    return () => {
      if (originalContent) {
        viewportMeta.setAttribute('content', originalContent);
      } else {
        viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1.0');
      }
    };
  }, []);
  
  // Загрузка данных для выбранного месяца
  useEffect(() => {
  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      
      // Проверяем наличие ID пользователя
      if (!telegramUserId) {
        console.warn("[Dashboard] Загрузка данных отменена: ID пользователя отсутствует");
        setIsLoading(false);
        return;
      }
      
      console.log('[Dashboard] Загрузка данных для месяца:', currentMonth, 'ID пользователя:', telegramUserId, 'Включить общие данные:', includeSharedData);
      const data = await fetchDashboardData(currentMonth, includeSharedData);
      console.log('[Dashboard] Полученные данные дашборда:', data);
      setDashboardData(data);
    } catch (error) {
      console.error('[Dashboard] Ошибка загрузки данных:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const checkLinkedAccounts = async () => {
    try {
      // Проверяем наличие ID пользователя
      if (!telegramUserId) {
        console.warn("[Dashboard] Проверка связанных аккаунтов отменена: ID пользователя отсутствует");
        setHasLinkedAccounts(false);
        return;
      }
      
      console.log(`[Dashboard] Проверка связанных аккаунтов для пользователя: ${telegramUserId}`);
      
      // Используем URL с параметрами вместо шаблонной строки для предотвращения "null" значений
      const url = new URL('https://fftg.farodinprojects.org:3000/get_linked_accounts');
      url.searchParams.append('user_id', telegramUserId);
      
      const response = await fetch(url.toString(), {
        headers: {
          'X-User-ID': telegramUserId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Если есть хотя бы один связанный аккаунт
        const hasAccounts = Array.isArray(data) && data.length > 0;
        setHasLinkedAccounts(hasAccounts);
        console.log(`[Dashboard] Найдено ${hasAccounts ? data.length : 0} связанных аккаунтов:`, data);
      } else {
        console.error("[Dashboard] Ошибка при проверке связанных аккаунтов:", response.status);
        setHasLinkedAccounts(false);
      }
    } catch (error) {
      console.error("[Dashboard] Ошибка при проверке связанных аккаунтов:", error);
      setHasLinkedAccounts(false);
    }
  };

  // Запускаем загрузку данных только если ID пользователя существует
  if (telegramUserId) {
    loadDashboardData();
    // Проверяем связанные аккаунты только один раз при монтировании компонента
    if (!hasLinkedAccounts) {
      checkLinkedAccounts();
    }
  } else {
    console.warn("[Dashboard] ID пользователя не доступен, загрузка данных отложена");
    setIsLoading(false);
  }
}, [currentMonth, includeSharedData, telegramUserId]); // Добавляем includeSharedData в зависимости

  // Обработчик нажатия на аватар пользователя
  const handleAvatarClick = () => {
    navigate('/settings'); // Переход на страницу настроек
  };

  const handleOpenModal = (type) => {
    setTransactionType(type);
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
  };

  const handleAddTransaction = async (transactionData) => {
    try {
      // Проверяем наличие ID пользователя
      if (!telegramUserId) {
        console.error("[Dashboard] Невозможно добавить транзакцию: ID пользователя отсутствует");
        alert(t('transaction_error'));
        return;
      }
      
      const endpoint = transactionType === 'income' 
        ? 'https://fftg.farodinprojects.org:3000/add_income'
        : 'https://fftg.farodinprojects.org:3000/add_expense';
        
      console.log(`[Dashboard] Отправка ${transactionType} для пользователя: ${telegramUserId}`);
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-ID': telegramUserId
        },
        body: JSON.stringify({
          user_id: telegramUserId,
          ...transactionData
        })
      });

      if (response.ok) {
        // После успешного добавления транзакции обновляем данные
        const data = await fetchDashboardData(currentMonth, includeSharedData);
        setDashboardData(data);
      } else {
        const errorData = await response.json();
        console.error("[Dashboard] Ошибка ответа сервера:", errorData);
        alert(t('transaction_error'));
      }
    } catch (error) {
      console.error("[Dashboard] Ошибка при добавлении транзакции:", error);
      alert(t('transaction_network_error'));
    }
  };

  // Расчет ежедневного бюджета
  const calculateDailyBudget = () => {
    if (dashboardData.daysToSalary <= 0 || dashboardData.balance.amount <= 0) {
      return 0;
    }
    return (dashboardData.balance.amount / dashboardData.daysToSalary).toFixed(2);
  };

  const dailyBudget = calculateDailyBudget();

  // Получаем или генерируем URL аватарки пользователя
  const avatarUrl = userPhotoUrl || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(telegramUserId || 'guest')}`;

  // Показываем загрузку, если контекст Telegram еще не инициализирован
  if (!isInitialized) {
    return (
      <Layout hideHeader={true}>
        <div className={commonStyles.container} style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100vh' 
        }}>
          <LoadingStatusText>{t('initializing_app') || 'Инициализация приложения...'}</LoadingStatusText>
        </div>
      </Layout>
    );
  }
  
  // Показываем сообщение об ошибке, если ID пользователя отсутствует
  if (!telegramUserId) {
    return (
      <Layout hideHeader={true}>
        <AuthErrorDisplay 
          t={t} 
          onRefresh={() => window.location.reload()} 
        />
      </Layout>
    );
  }

return (
  <Layout hideHeader={true}>
    <NoZoomContainer>
      <div className={commonStyles.container}>
        {/* Упрощенный заголовок только с селектором месяца и аватаркой */}
        <HeaderContainer>
          <HeaderCenter>
            <div className={dashboardStyles.monthSelectorContainer}>
              <MonthSelector 
                currentMonth={currentMonth} 
                onChangeMonth={setCurrentMonth} 
              />
            </div>
          </HeaderCenter>
          
          <HeaderRight>
            {/* Только аватарка пользователя */}
            <UserAvatar onClick={handleAvatarClick}>
              <img src={avatarUrl} alt={t('user_settings')} />
            </UserAvatar>
          </HeaderRight>
        </HeaderContainer>
          
        {/* Курс валют с уведомлениями */}
        <ExchangeRateWithNotifications>
          <ExchangeRate />
          <NotificationIconContainer>
            <NotificationCenter />
          </NotificationIconContainer>
        </ExchangeRateWithNotifications>

          {/* Остальной контент страницы без изменений */}
          {hasLinkedAccounts && (
            <StyledSwitchContainer>
              <SwitchLabel>{t('include_shared_data')}</SwitchLabel>
              <SwitchToggle>
                <input 
                  type="checkbox" 
                  checked={includeSharedData}
                  onChange={() => setIncludeSharedData(!includeSharedData)} 
                />
                <span className="slider"></span>
              </SwitchToggle>
            </StyledSwitchContainer>
          )}

{isLoading ? (
  <>
    <LoadingStatusText>{t('loading_data')}</LoadingStatusText>
    <div className={commonStyles.cardGrid}>
      {/* Первая карточка с имитацией бюджета */}
      <SkeletonCard>
        <SkeletonTitle />
        <SkeletonText />
        <SkeletonBudget />
      </SkeletonCard>
      
      {/* Остальные карточки */}
      {[1, 2, 3].map((item) => (
        <SkeletonCard key={item}>
          <SkeletonTitle />
          <SkeletonText />
          {/* Случайно добавляем дополнительный элемент для разнообразия */}
          {Math.random() > 0.5 && <SkeletonBudget />}
        </SkeletonCard>
      ))}
    </div>
    
    <div className={commonStyles.buttonGroup}>
      <SkeletonButton />
      <SkeletonButton />
    </div>
    
    <SkeletonNavContainer>
      <SkeletonNavButton />
      <SkeletonNavButton />
    </SkeletonNavContainer>
  </>
) : (
  <>
    <div className={commonStyles.cardGrid}>
            <EnhancedCard 
              type="balance" 
              $negative={dashboardData.balance.amount < 0}
            >
              <h2>{formatCurrency(dashboardData.balance.amount, dashboardData.balance.currency)}</h2>
              <p>{t('balance')}</p>
              {dashboardData.balance.amount < 0 ? (
                <NegativeBalanceIndicator>
                  <WarningIcon />
                  {t('add_income_to_fix')}
                </NegativeBalanceIndicator>
              ) : dailyBudget > 0 && (
                <DailyBudgetInfo>
                  <BudgetLabel>{t('daily_budget')}:</BudgetLabel>
                  <BudgetValue>
                    {formatCurrency(dailyBudget, dashboardData.balance.currency)}
                  </BudgetValue>
                </DailyBudgetInfo>
              )}
            </EnhancedCard>
            
            <EnhancedCard type="income">
              <h2>{formatCurrency(dashboardData.income.amount, dashboardData.income.currency)}</h2>
              <p>{t('income')}</p>
              {dashboardData.incomeComparison !== null && (
                <ComparisonInfo type={dashboardData.incomeComparison > 0 ? 'positive' : 'negative'}>
                  {dashboardData.incomeComparison > 0 
                    ? `${t('moreThanLastMonth')} ${Math.abs(dashboardData.incomeComparison).toFixed(1)}%` 
                    : `${t('lessThanLastMonth')} ${Math.abs(dashboardData.incomeComparison).toFixed(1)}%`}
                </ComparisonInfo>
              )}
            </EnhancedCard>
            
            <EnhancedCard type="expense">
              <h2>{formatCurrency(dashboardData.expenses.amount, dashboardData.expenses.currency)}</h2>
              <p>{t('expenses')}</p>
              {dashboardData.expensesComparison !== null && (
                <ComparisonInfo type={dashboardData.expensesComparison > 0 ? 'negative' : 'positive'}>
                  {dashboardData.expensesComparison > 0 
                    ? `${t('moreThanLastMonth')} ${Math.abs(dashboardData.expensesComparison).toFixed(1)}%` 
                    : `${t('lessThanLastMonth')} ${Math.abs(dashboardData.expensesComparison).toFixed(1)}%`}
                </ComparisonInfo>
              )}
            </EnhancedCard>
            
            <DaysToSalaryCard 
              $daysPercent={Math.min(100, (30 - dashboardData.daysToSalary) / 30 * 100)}
            >
              <div className="days-circle">
                <div className="circle-bg"></div>
                <div className="circle-progress"></div>
                <div className="circle-inner">
                  <span className="days-number">{dashboardData.daysToSalary}</span>
                </div>
              </div>
              <p>{t('days_to_salary')}</p>
            </DaysToSalaryCard>
          </div>

          <ActionButtonsContainer>
            <ActionButton 
              type="income"
              onClick={() => handleOpenModal('income')}
            >
              <PlusIcon />
              {t('add_income')}
            </ActionButton>
            <ActionButton 
              type="expense"
              onClick={() => handleOpenModal('expense')}
            >
              <MinusIcon />
              {t('add_expense')}
            </ActionButton>
          </ActionButtonsContainer>

          <NavButtonsContainer>
            <NavButton onClick={() => navigate('/statistics')}>
              <StatsIcon />
              {t('statistics')}
            </NavButton>
            <NavButton onClick={() => navigate('/goals')}>
              <GoalsIcon />
              {t('goals')}
            </NavButton>
          </NavButtonsContainer>
        </>
      )}
        {/* Модальное окно для добавления транзакции */}
          <TransactionModal
            isOpen={showAddModal}
            onClose={handleCloseModal}
            type={transactionType}
            onAddTransaction={handleAddTransaction}
          />
        </div>
      </NoZoomContainer>
    </Layout>
  );
};

export default Dashboard;